// based on https://github.com/sonyan/react-wysiwyg-editor/blob/master/EditableDiv.jsx

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FormatAlignCenter as FormatAlignCenterIcon,
  FormatAlignJustify as FormatAlignJustifyIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignRight as FormatAlignRightIcon,
  FormatBold as FormatBoldIcon,
  FormatClear as FormatClearIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  TextDecrease as TextDecreaseIcon,
} from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import PropTypes from 'prop-types'
import { useCanEdit } from '~/hooks'
import ActionButton from './ActionButton'

function getTextOffset(editor, node, offset) {
  const range = document.createRange()
  range.setStart(editor, 0)
  range.setEnd(node, offset)
  return range.toString().length
}

function getNodeAndOffsetAtTextOffset(editor, textOffset) {
  const walker = document.createTreeWalker(
    editor,
    NodeFilter.SHOW_TEXT,
    null,
    false
  )
  let count = 0
  let lastNode = null
  let lastLen = 0
  let node
  while ((node = walker.nextNode())) {
    lastNode = node
    lastLen = node.length
    if (textOffset <= count + lastLen) {
      return { node, offset: textOffset - count }
    }
    count += lastLen
  }
  if (lastNode) return { node: lastNode, offset: lastLen }
  return { node: editor, offset: editor.childNodes.length }
}

const HTMLEditor = (props) => {
  const {
    disabled: disabledProp,
    highlighted,
    sx,
    text: textProp,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    ...rest
  } = props
  const { t } = useTranslation()
  const canEdit = useCanEdit()
  const disabled = disabledProp || !canEdit

  const editorRef = useRef()

  // Sync from parent only when content actually differs (e.g. load from server).
  // Never overwrite on re-render after user typing – that would reset the cursor.
  useEffect(() => {
    if (!editorRef.current) return
    const current = editorRef.current.innerHTML
    const next = textProp ?? ''
    if (current !== next) {
      editorRef.current.innerHTML = next
    }
  }, [textProp])

  const onPaste = (e) => {
    // Stop data actually being pasted into div
    e.stopPropagation()
    e.preventDefault()

    // Get pasted data via clipboard API
    let clipboardData = e.clipboardData || window.clipboardData
    let pastedData = clipboardData.getData('Text')

    // Do whatever with pasteddata
    // console.log(pastedData);

    const selectPastedContent = true

    // http://stackoverflow.com/questions/6690752/insert-html-at-caret-in-a-contenteditable-div
    var sel, range
    if (window.getSelection) {
      // IE9 and non-IE
      sel = window.getSelection()
      if (sel.getRangeAt && sel.rangeCount) {
        range = sel.getRangeAt(0)
        range.deleteContents()

        // Range.createContextualFragment() would be useful here but is
        // only relatively recently standardized and is not supported in
        // some browsers (IE9, for one)
        var el = document.createElement('div')
        el.innerHTML = pastedData
        var frag = document.createDocumentFragment(),
          node,
          lastNode
        while ((node = el.firstChild)) {
          lastNode = frag.appendChild(node)
        }
        var firstNode = frag.firstChild
        range.insertNode(frag)

        // Preserve the selection
        if (lastNode) {
          range = range.cloneRange()
          range.setStartAfter(lastNode)
          if (selectPastedContent) {
            range.setStartBefore(firstNode)
          } else {
            range.collapse(true)
          }
          sel.removeAllRanges()
          sel.addRange(range)
        }
      }
    } else if ((sel = document.selection) && sel.type !== 'Control') {
      // IE < 9
      var originalRange = sel.createRange()
      originalRange.collapse(true)
      sel.createRange().pasteHTML(pastedData)
      if (selectPastedContent) {
        range = sel.createRange()
        range.setEndPoint('StartToStart', originalRange)
        range.select()
      }
    }

    // setText(editor.current.innerHTML)
    onChange?.({ target: { value: editorRef.current.innerHTML.trim() } })
  }

  const execCommand = (command, arg) => {
    const editor = editorRef.current
    if (!editor) return
    editor.focus()

    const sel = window.getSelection()
    let startOffset = 0
    let endOffset = 0
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      startOffset = getTextOffset(
        editor,
        range.startContainer,
        range.startOffset
      )
      endOffset = getTextOffset(editor, range.endContainer, range.endOffset)
    }

    var result = document.execCommand(command, false, arg)
    if (!result) {
      console.warn(`${command}(${arg}) was not executed`)
    }
    if (command === 'fontSize') {
      var node = window.getSelection().focusNode?.parentNode
      if (node) {
        node.removeAttribute('size')
        node.classList.add('small')
      }
    }

    const restoreSelection = () => {
      const start = getNodeAndOffsetAtTextOffset(editor, startOffset)
      const end = getNodeAndOffsetAtTextOffset(editor, endOffset)
      const range = document.createRange()
      range.setStart(start.node, start.offset)
      range.setEnd(end.node, end.offset)
      sel.removeAllRanges()
      sel.addRange(range)
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(restoreSelection)
    })
  }

  const handleToolbarMouseDown = (e) => {
    e.preventDefault()
  }

  const [focused, setFocused] = useState(false)

  return (
    <Stack spacing={0.5} sx={sx}>
      <Box
        sx={{
          position: 'relative',
          minHeight: '100px',
          ...(focused
            ? {}
            : {
                '&:hover .notchedOutline': {
                  border: (theme) =>
                    `1px solid ${theme.palette.input.border.hover}`,
                },
              }),
        }}
      >
        <Box
          ref={editorRef}
          {...rest}
          contentEditable={disabled ? undefined : 'true'}
          suppressContentEditableWarning
          sx={{
            padding: '8.5px 14px',
            overflow: 'auto',
            '&:focus': {
              outline: 'none',
            },
          }}
          onBlur={() => {
            setFocused(false)
            onBlur?.()
          }}
          onFocus={() => {
            setFocused(true)
            onFocus?.()
          }}
          onInput={(e) => {
            const html = e.target.innerHTML
            onChange?.({ target: { value: html === '<br>' ? '' : html } })
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        <Box
          aria-hidden
          className="notchedOutline"
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            borderRadius: '4px',
            ...(focused
              ? {
                  border: (theme) =>
                    `2px solid ${theme.palette.input.border.focused}`,
                }
              : {
                  border: (theme) =>
                    highlighted
                      ? `2px solid ${theme.palette.input.border.highlighted}`
                      : `1px solid ${theme.palette.input.border.default}`,
                }),
          }}
        />
      </Box>

      {disabled ? null : (
        <Stack direction="row" spacing={1}>
          <Stack direction="row" spacing={0.25}>
            <ActionButton
              background
              disabled={disabled}
              icon={<FormatBoldIcon />}
              size="small"
              title={t('wyswig.bold')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('bold')}
            />
            <ActionButton
              background
              disabled={disabled}
              icon={<FormatItalicIcon />}
              size="small"
              title={t('wyswig.italic')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('italic')}
            />
            <ActionButton
              background
              disabled={disabled}
              icon={<FormatUnderlinedIcon />}
              size="small"
              title={t('wyswig.underline')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('underline')}
            />
            <ActionButton
              background
              disabled={disabled}
              size="small"
              icon={<TextDecreaseIcon />}
              title={t('wyswig.small-font-size')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('fontSize', '1')}
            />
            <ActionButton
              background
              disabled={disabled}
              size="small"
              icon={<FormatClearIcon />}
              title={t('wyswig.clear-format')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('removeFormat')}
            />
          </Stack>
          <Stack direction="row" spacing={0.25}>
            <ActionButton
              background
              disabled={disabled}
              size="small"
              icon={<FormatAlignLeftIcon />}
              title={t('wyswig.align-left')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('justifyLeft')}
            />
            <ActionButton
              background
              disabled={disabled}
              size="small"
              icon={<FormatAlignCenterIcon />}
              title={t('wyswig.align-center')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('justifyCenter')}
            />
            <ActionButton
              background
              disabled={disabled}
              size="small"
              icon={<FormatAlignRightIcon />}
              title={t('wyswig.align-right')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('justifyRight')}
            />
            <ActionButton
              background
              disabled={disabled}
              size="small"
              icon={<FormatAlignJustifyIcon />}
              title={t('wyswig.align-justify')}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => execCommand('justifyFull')}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

HTMLEditor.propTypes = {
  disabled: PropTypes.bool,
  highlighted: PropTypes.bool,
  sx: PropTypes.object,
  text: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
}

export default HTMLEditor
